import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { Sprout, Shield, Users, Zap, BarChart3, LogOut, Menu, X } from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete before scrolling
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const offset = 80; // Account for fixed navbar height
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 80; // Account for fixed navbar height
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const scrollToContactForm = () => {
    scrollToSection('contact-form');
  };

  return (
    <nav className="sticky top-0 w-full bg-white/95 backdrop-blur-sm border-b border-neo-emerald/20 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="bg-white p-2 rounded">
                <img 
                  src="/BLOOMCYCLE_INNOVATIONS-removebg-preview.webp" 
                  alt="BLOOMCYCLE INNOVATIONS"
                  className="h-12 w-auto object-contain"
                />
              </div>
            </Link>
          </div>
          
          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('about')}
              className="text-neo-gray hover:text-neo-emerald transition-colors font-body text-sm"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('process')}
              className="text-neo-gray hover:text-neo-emerald transition-colors font-body text-sm"
            >
              Process
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="text-neo-gray hover:text-neo-emerald transition-colors font-body text-sm"
            >
              Features
            </button>
            <button
              onClick={() => scrollToSection('mrv')}
              className="text-neo-gray hover:text-neo-emerald transition-colors font-body text-sm"
            >
              MRV
            </button>
            <button
              onClick={() => scrollToSection('team')}
              className="text-neo-gray hover:text-neo-emerald transition-colors font-body text-sm"
            >
              Team
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-neo-gray hover:text-neo-emerald transition-colors font-body text-sm"
            >
              Contact
            </button>
          </div>

          {/* Desktop Trust Badges */}
          <div className="hidden md:flex lg:hidden items-center gap-4 text-xs">
            <div className="flex items-center gap-1 text-neo-gray">
              <Shield className="w-3 h-3 text-neo-emerald" />
              <span className="font-body">DPIIT</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Contact Form Button */}
            <Button
              onClick={scrollToContactForm}
              className="hidden md:flex bg-neo-emerald hover:bg-[#1db868] text-neo-white glow-emerald-hover transition-all duration-300"
            >
              Get in Touch
            </Button>

            {user ? (
              <div className="flex items-center gap-3">
                <span className="hidden sm:inline text-sm text-neo-gray font-body">Welcome, {user.name}</span>
                <Button 
                  onClick={handleLogout}
                  variant="outline" 
                  size="sm"
                  className="flex items-center gap-2 border-neo-emerald/30 text-neo-gray hover:text-neo-black hover:border-neo-emerald"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            ) : (
              <Link to="/login" className="hidden">
                <Button className="bg-neo-emerald hover:bg-[#1db868] text-neo-white glow-emerald-hover">
                  Get Started
                </Button>
              </Link>
            )}
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-neo-gray hover:text-neo-emerald transition-colors p-2"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-neo-emerald/20 bg-white">
            <div className="px-4 py-4 space-y-3">
              <button
                onClick={() => scrollToSection('about')}
                className="block w-full text-left text-neo-gray hover:text-neo-emerald transition-colors font-body py-2"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('process')}
                className="block w-full text-left text-neo-gray hover:text-neo-emerald transition-colors font-body py-2"
              >
                Process
              </button>
              <button
                onClick={() => scrollToSection('features')}
                className="block w-full text-left text-neo-gray hover:text-neo-emerald transition-colors font-body py-2"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection('mrv')}
                className="block w-full text-left text-neo-gray hover:text-neo-emerald transition-colors font-body py-2"
              >
                MRV
              </button>
              <button
                onClick={() => scrollToSection('team')}
                className="block w-full text-left text-neo-gray hover:text-neo-emerald transition-colors font-body py-2"
              >
                Team
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left text-neo-gray hover:text-neo-emerald transition-colors font-body py-2"
              >
                Contact
              </button>
              <Button
                onClick={scrollToContactForm}
                className="w-full mt-2 bg-neo-emerald hover:bg-[#1db868] text-neo-white glow-emerald-hover"
              >
                Get in Touch
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
